// src/pages/modals/PinConfirmModal.js

import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  Fade,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import SecurityRoundedIcon from '@mui/icons-material/SecurityRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import WifiOffRoundedIcon from '@mui/icons-material/WifiOffRounded';
import api from '../../api/api';

/* ======================================================================
 * AUTORIZACIÓN PIN DE CONTINGENCIA
 *
 * No se guarda el PIN en texto plano ni un hash reutilizable directamente.
 * Cuando el servidor valida correctamente un PIN:
 *   1) se deriva una clave con PBKDF2 + salt aleatorio;
 *   2) el resultado se firma con una CryptoKey HMAC no extraíble del equipo;
 *   3) solo se guarda salt + firma + perfil autorizado en IndexedDB.
 *
 * Así, un PIN que ya fue autorizado online en ESTE navegador puede volver a
 * verificarse durante una caída de la API. La autorización local expira para
 * limitar la vigencia de credenciales antiguas.
 * ==================================================================== */

const PIN_SECURITY_DB = 'IntegraNubePOSSecurity';
const PIN_SECURITY_DB_VERSION = 1;
const PIN_KEYS_STORE = 'device_keys';
const PIN_AUTH_STORE = 'pin_authorizations';
const DEVICE_HMAC_KEY_ID = 'pin_hmac_v1';
const OFFLINE_PIN_TTL_MS = 72 * 60 * 60 * 1000; // 72 horas
const PBKDF2_ITERATIONS = 210000;
const OFFLINE_MAX_FAILED_ATTEMPTS = 5;
const OFFLINE_LOCK_MS = 60 * 1000;

function openPinSecurityDb() {
  return new Promise((resolve, reject) => {
    if (!window.indexedDB) {
      reject(new Error('IndexedDB no está disponible en este navegador.'));
      return;
    }

    const request = window.indexedDB.open(PIN_SECURITY_DB, PIN_SECURITY_DB_VERSION);

    request.onupgradeneeded = () => {
      const db = request.result;

      if (!db.objectStoreNames.contains(PIN_KEYS_STORE)) {
        db.createObjectStore(PIN_KEYS_STORE, { keyPath: 'id' });
      }

      if (!db.objectStoreNames.contains(PIN_AUTH_STORE)) {
        const store = db.createObjectStore(PIN_AUTH_STORE, { keyPath: 'id' });
        store.createIndex('tenantid', 'tenantid', { unique: false });
        store.createIndex('expiresAt', 'expiresAt', { unique: false });
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error || new Error('No se pudo abrir la caché segura de PIN.'));
  });
}

function idbRequest(request) {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error || new Error('Error de IndexedDB.'));
  });
}

async function getOrCreateDeviceHmacKey() {
  if (!window.crypto?.subtle) {
    throw new Error('Web Crypto no está disponible.');
  }

  const db = await openPinSecurityDb();

  try {
    const txRead = db.transaction(PIN_KEYS_STORE, 'readonly');
    const existing = await idbRequest(txRead.objectStore(PIN_KEYS_STORE).get(DEVICE_HMAC_KEY_ID));

    if (existing?.key) return existing.key;

    const key = await window.crypto.subtle.generateKey(
      { name: 'HMAC', hash: 'SHA-256', length: 256 },
      false,
      ['sign', 'verify']
    );

    const txWrite = db.transaction(PIN_KEYS_STORE, 'readwrite');
    await idbRequest(
      txWrite.objectStore(PIN_KEYS_STORE).put({
        id: DEVICE_HMAC_KEY_ID,
        key,
        createdAt: new Date().toISOString(),
      })
    );

    return key;
  } finally {
    db.close();
  }
}

function bytesToBase64(bytes) {
  let binary = '';
  const arr = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  for (let i = 0; i < arr.length; i += 1) binary += String.fromCharCode(arr[i]);
  return window.btoa(binary);
}

function base64ToBytes(value) {
  const binary = window.atob(String(value || ''));
  const out = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) out[i] = binary.charCodeAt(i);
  return out;
}

async function derivePinMaterial(pin, saltBytes) {
  const encoder = new TextEncoder();
  const baseKey = await window.crypto.subtle.importKey(
    'raw',
    encoder.encode(pin),
    'PBKDF2',
    false,
    ['deriveBits']
  );

  const bits = await window.crypto.subtle.deriveBits(
    {
      name: 'PBKDF2',
      hash: 'SHA-256',
      salt: saltBytes,
      iterations: PBKDF2_ITERATIONS,
    },
    baseKey,
    256
  );

  return new Uint8Array(bits);
}

async function createPinVerifier(pin, saltBytes) {
  const hmacKey = await getOrCreateDeviceHmacKey();
  const material = await derivePinMaterial(pin, saltBytes);
  const signature = await window.crypto.subtle.sign('HMAC', hmacKey, material);
  return new Uint8Array(signature);
}

function constantTimeEqual(a, b) {
  const x = a instanceof Uint8Array ? a : new Uint8Array(a || []);
  const y = b instanceof Uint8Array ? b : new Uint8Array(b || []);
  if (x.length !== y.length) return false;

  let diff = 0;
  for (let i = 0; i < x.length; i += 1) diff |= x[i] ^ y[i];
  return diff === 0;
}

function decodeJwtPayload(token) {
  try {
    const payloadPart = String(token || '').split('.')[1];
    if (!payloadPart) return null;
    const normalized = payloadPart.replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized + '='.repeat((4 - (normalized.length % 4)) % 4);
    return JSON.parse(window.atob(padded));
  } catch {
    return null;
  }
}

async function cacheVerifiedPin({ pin, tenantid, data }) {
  if (!pin || !tenantid || !window.crypto?.subtle) return false;

  const tokenPayload = decodeJwtPayload(data?.token);
  const ownerId = tokenPayload?.sub != null ? String(tokenPayload.sub) : null;
  if (!ownerId) return false;

  const salt = new Uint8Array(16);
  window.crypto.getRandomValues(salt);
  const verifier = await createPinVerifier(pin, salt);
  const now = Date.now();

  const record = {
    id: `${tenantid}:${ownerId}`,
    tenantid,
    ownerId,
    salt: bytesToBase64(salt),
    verifier: bytesToBase64(verifier),
    profile: data?.profile || null,
    cachedAt: new Date(now).toISOString(),
    expiresAt: new Date(now + OFFLINE_PIN_TTL_MS).toISOString(),
    version: 1,
  };

  const db = await openPinSecurityDb();
  try {
    const tx = db.transaction(PIN_AUTH_STORE, 'readwrite');
    await idbRequest(tx.objectStore(PIN_AUTH_STORE).put(record));
    return true;
  } finally {
    db.close();
  }
}

async function getCachedPinAuthorizations(tenantid) {
  const db = await openPinSecurityDb();
  try {
    const tx = db.transaction(PIN_AUTH_STORE, 'readwrite');
    const store = tx.objectStore(PIN_AUTH_STORE);
    const index = store.index('tenantid');
    const rows = await idbRequest(index.getAll(tenantid));
    const now = Date.now();
    const valid = [];

    for (const row of rows || []) {
      const expires = new Date(row?.expiresAt || 0).getTime();
      if (!Number.isFinite(expires) || expires <= now) {
        try {
          store.delete(row.id);
        } catch {
          // No bloquear contingencia si la limpieza falla.
        }
      } else {
        valid.push(row);
      }
    }

    return valid;
  } finally {
    db.close();
  }
}

async function removeCachedPinAuthorization(id) {
  if (!id) return;
  const db = await openPinSecurityDb();
  try {
    const tx = db.transaction(PIN_AUTH_STORE, 'readwrite');
    await idbRequest(tx.objectStore(PIN_AUTH_STORE).delete(id));
  } finally {
    db.close();
  }
}

async function verifyPinOffline(pin, tenantid) {
  if (!window.crypto?.subtle) {
    return { ok: false, reason: 'crypto_unavailable' };
  }

  const rows = await getCachedPinAuthorizations(tenantid);
  if (!rows.length) return { ok: false, reason: 'empty_cache' };

  for (const row of rows) {
    try {
      const salt = base64ToBytes(row.salt);
      const expected = base64ToBytes(row.verifier);
      const actual = await createPinVerifier(pin, salt);

      if (constantTimeEqual(actual, expected)) {
        return {
          ok: true,
          record: row,
          data: {
            token: null,
            profile: row.profile || null,
            tenantid,
            offline: true,
            authorizationMode: 'offline-cache',
            authorizedBy: row.ownerId || null,
            cachedAt: row.cachedAt || null,
            expiresAt: row.expiresAt || null,
          },
        };
      }
    } catch (err) {
      console.warn('[PIN offline] No se pudo verificar un registro local:', err);
    }
  }

  return { ok: false, reason: 'invalid_pin', rows };
}

function isInfrastructurePinError(err) {
  if (!err) return true;
  if (!err.response) return true;

  const status = Number(err.response?.status || 0);
  if ([408, 500, 502, 503, 504].includes(status)) return true;
  if (status >= 520 && status <= 530) return true;

  const code = String(err.code || '').toUpperCase();
  return ['ERR_NETWORK', 'ECONNABORTED', 'ETIMEDOUT'].includes(code);
}

function getRateLimitKey(tenantid) {
  return `integranube_pos_pin_offline_rl:${tenantid}`;
}

function readRateLimit(tenantid) {
  try {
    const raw = localStorage.getItem(getRateLimitKey(tenantid));
    if (!raw) return { attempts: 0, lockedUntil: 0 };
    const parsed = JSON.parse(raw);
    return {
      attempts: Number(parsed?.attempts || 0),
      lockedUntil: Number(parsed?.lockedUntil || 0),
    };
  } catch {
    return { attempts: 0, lockedUntil: 0 };
  }
}

function clearRateLimit(tenantid) {
  try {
    localStorage.removeItem(getRateLimitKey(tenantid));
  } catch {
    // noop
  }
}

function registerOfflinePinFailure(tenantid) {
  const state = readRateLimit(tenantid);
  const attempts = state.attempts + 1;
  const locked = attempts >= OFFLINE_MAX_FAILED_ATTEMPTS;
  const next = {
    attempts: locked ? 0 : attempts,
    lockedUntil: locked ? Date.now() + OFFLINE_LOCK_MS : 0,
  };

  try {
    localStorage.setItem(getRateLimitKey(tenantid), JSON.stringify(next));
  } catch {
    // noop
  }

  return next;
}

/**
 * Solicita aprobación mediante PIN para acciones críticas.
 *
 * Online: POST /auth/pin-login (fuente de verdad).
 * Contingencia: solo si la API es inalcanzable, usa una autorización que haya
 * sido validada online anteriormente en este mismo equipo.
 */
const PinConfirmModal = ({
  open,
  onClose,
  onVerified,
  title = 'Confirmar acción',
  message = 'Se requiere autorización de supervisor o administrador.',
}) => {
  const [pin, setPin] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [contingencyMsg, setContingencyMsg] = useState('');

  const inputRef = useRef(null);
  const dialogContentRef = useRef(null);

  useEffect(() => {
    if (open) {
      setPin('');
      setErrorMsg('');
      setContingencyMsg('');
      setIsSubmitting(false);

      setTimeout(() => {
        inputRef.current?.focus();
      }, 80);
    }
  }, [open]);

  const pinSanitized = useMemo(() => pin.trim(), [pin]);

  const isPinValid = useMemo(() => {
    return /^[a-zA-Z0-9]{4,}$/.test(pinSanitized);
  }, [pinSanitized]);

  const isUuid = (value) => {
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
      String(value || '').trim()
    );
  };

  const normalizeTenantId = (value) => {
    const clean = String(value || '').trim();
    return isUuid(clean) ? clean : null;
  };

  const safeJsonParse = (key) => {
    try {
      const raw = localStorage.getItem(key);
      if (!raw) return null;
      return JSON.parse(raw);
    } catch {
      return null;
    }
  };

  const getTenantId = () => {
    const fromFlat = normalizeTenantId(localStorage.getItem('tenantid'));
    if (fromFlat) return fromFlat;

    const tenant = safeJsonParse('tenant');
    const fromTenantObject = normalizeTenantId(tenant?.tenantid || tenant?.tenantId || tenant?.id);
    if (fromTenantObject) return fromTenantObject;

    const user = safeJsonParse('user');
    const fromUserObject = normalizeTenantId(user?.tenantid || user?.tenantId);
    if (fromUserObject) return fromUserObject;

    return null;
  };

  const resetAndFocusPin = () => {
    setPin('');
    requestAnimationFrame(() => {
      inputRef.current?.focus();
      inputRef.current?.select?.();
    });
  };

  const stopKeyboardEvent = (e) => {
    e.stopPropagation();
    if (e.nativeEvent?.stopImmediatePropagation) {
      e.nativeEvent.stopImmediatePropagation();
    }
  };

  const handleOfflineFallback = async ({ tenantid, originalError }) => {
    const rate = readRateLimit(tenantid);
    if (rate.lockedUntil > Date.now()) {
      const seconds = Math.max(1, Math.ceil((rate.lockedUntil - Date.now()) / 1000));
      setContingencyMsg('Validación local de contingencia activa.');
      setErrorMsg(`Demasiados intentos de PIN. Espera ${seconds} segundos e intenta nuevamente.`);
      resetAndFocusPin();
      return;
    }

    setContingencyMsg('API no disponible · validando autorización local del equipo…');

    try {
      const offline = await verifyPinOffline(pinSanitized, tenantid);

      if (offline.ok) {
        clearRateLimit(tenantid);
        setErrorMsg('');
        setContingencyMsg('PIN autorizado localmente en modo contingencia.');

        onVerified({
          ...offline.data,
          infrastructureError: originalError?.response?.status || originalError?.code || 'offline',
        });

        onClose();
        return;
      }

      if (offline.reason === 'empty_cache') {
        setErrorMsg(
          'No hay PIN de supervisor preparado para contingencia en este equipo. Valídelo al menos una vez mientras la API esté disponible.'
        );
      } else if (offline.reason === 'crypto_unavailable') {
        setErrorMsg('Este navegador no permite validar el PIN localmente.');
      } else {
        const nextRate = registerOfflinePinFailure(tenantid);
        if (nextRate.lockedUntil > Date.now()) {
          setErrorMsg('PIN inválido. Se bloqueó la validación local durante 60 segundos por seguridad.');
        } else {
          const remaining = OFFLINE_MAX_FAILED_ATTEMPTS - nextRate.attempts;
          setErrorMsg(`PIN inválido o no habilitado para contingencia en este equipo. Intentos restantes: ${remaining}.`);
        }
      }

      resetAndFocusPin();
    } catch (offlineErr) {
      console.error('[PIN offline] Error:', offlineErr);
      setErrorMsg('No se pudo acceder a la autorización local del PIN.');
      resetAndFocusPin();
    }
  };

  const handleSubmit = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    if (e?.stopPropagation) e.stopPropagation();
    if (e?.nativeEvent?.stopImmediatePropagation) {
      e.nativeEvent.stopImmediatePropagation();
    }

    if (isSubmitting) return;

    if (!isPinValid) {
      setErrorMsg('El PIN debe tener al menos 4 caracteres alfanuméricos.');
      resetAndFocusPin();
      return;
    }

    const tenantid = getTenantId();

    if (!tenantid) {
      setErrorMsg(
        'Error interno: no se encontró tenantid válido. Vuelve a seleccionar la empresa e intenta nuevamente.'
      );
      resetAndFocusPin();
      return;
    }

    setIsSubmitting(true);
    setErrorMsg('');
    setContingencyMsg('');

    try {
      const response = await api.post(
        '/auth/pin-login',
        {
          pin: pinSanitized,
          tenantid,
        },
        {
          headers: { tenantid },
          timeout: 5000,
        }
      );

      const data = response.data || {};

      if (data.token) {
        localStorage.setItem('pinToken', data.token);
      }

      // La API es la fuente de verdad. Solo después de una validación real se
      // habilita este mismo PIN para futuras caídas en este equipo.
      try {
        await cacheVerifiedPin({ pin: pinSanitized, tenantid, data });
      } catch (cacheErr) {
        console.warn('[PIN] No se pudo preparar la autorización offline:', cacheErr);
      }

      clearRateLimit(tenantid);

      onVerified({
        ...data,
        tenantid,
        offline: false,
        authorizationMode: 'server',
      });

      onClose();
    } catch (err) {
      console.error('🔴 pin-login HTTP error:', err.response?.data || err);

      // IMPORTANTÍSIMO: si el servidor respondió 400/401/403, esa respuesta
      // manda. Nunca se permite que el cache local contradiga un PIN rechazado
      // por la API.
      if (isInfrastructurePinError(err)) {
        await handleOfflineFallback({ tenantid, originalError: err });
      } else {
        const serverMsg =
          err.response?.data?.error ||
          err.response?.data?.message ||
          err.friendlyMessage ||
          err.message;

        setErrorMsg(serverMsg || 'Error al verificar el PIN. Intenta nuevamente.');
        resetAndFocusPin();
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKeyDownCapture = (e) => {
    stopKeyboardEvent(e);

    if (e.key === 'Enter') {
      e.preventDefault();
      if (!isSubmitting) handleSubmit(e);
      return;
    }

    if (e.key === 'Escape') {
      e.preventDefault();
      if (!isSubmitting) onClose();
    }
  };

  const handleKeyUpCapture = (e) => stopKeyboardEvent(e);
  const handleKeyPressCapture = (e) => stopKeyboardEvent(e);

  useEffect(() => {
    if (!open) return undefined;

    const blockGlobalShortcuts = (e) => {
      const modalRoot = dialogContentRef.current;
      const activeEl = document.activeElement;
      const modalContainsFocus =
        modalRoot && (modalRoot.contains(activeEl) || modalRoot.contains(e.target));

      if (!modalContainsFocus) return;
      e.stopPropagation();
      if (e.stopImmediatePropagation) e.stopImmediatePropagation();
    };

    document.addEventListener('keydown', blockGlobalShortcuts, true);
    document.addEventListener('keyup', blockGlobalShortcuts, true);
    document.addEventListener('keypress', blockGlobalShortcuts, true);

    return () => {
      document.removeEventListener('keydown', blockGlobalShortcuts, true);
      document.removeEventListener('keyup', blockGlobalShortcuts, true);
      document.removeEventListener('keypress', blockGlobalShortcuts, true);
    };
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={isSubmitting ? undefined : onClose}
      maxWidth="xs"
      fullWidth
      TransitionComponent={Fade}
      disableEscapeKeyDown
      PaperProps={{
        sx: {
          borderRadius: 4,
          overflow: 'hidden',
          boxShadow: '0 24px 60px rgba(0,0,0,0.22)',
          backgroundImage: 'none',
        },
        onKeyDownCapture: handleKeyDownCapture,
        onKeyUpCapture: handleKeyUpCapture,
        onKeyPressCapture: handleKeyPressCapture,
      }}
    >
      <Box
        sx={{
          position: 'relative',
          px: 3,
          pt: 3,
          pb: 2,
          background:
            'linear-gradient(180deg, rgba(25,118,210,0.08) 0%, rgba(25,118,210,0.03) 100%)',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <IconButton
          onClick={onClose}
          disabled={isSubmitting}
          size="small"
          sx={{ position: 'absolute', top: 12, right: 12 }}
        >
          <CloseRoundedIcon fontSize="small" />
        </IconButton>

        <Stack direction="row" spacing={2} alignItems="center">
          <Box
            sx={{
              width: 52,
              height: 52,
              borderRadius: 3,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              boxShadow: '0 10px 24px rgba(25,118,210,0.28)',
            }}
          >
            <SecurityRoundedIcon />
          </Box>

          <Box sx={{ pr: 3 }}>
            <Typography variant="h6" fontWeight={800} lineHeight={1.1}>
              {title}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {message}
            </Typography>
          </Box>
        </Stack>
      </Box>

      <DialogContent
        ref={dialogContentRef}
        sx={{ px: 3, py: 3 }}
        onKeyDownCapture={handleKeyDownCapture}
        onKeyUpCapture={handleKeyUpCapture}
        onKeyPressCapture={handleKeyPressCapture}
      >
        <Box component="form" onSubmit={handleSubmit} noValidate autoComplete="off">
          <Stack spacing={2.25}>
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.1, fontWeight: 500 }}>
                Ingrese el PIN de autorización
              </Typography>

              <TextField
                inputRef={inputRef}
                fullWidth
                placeholder="PIN de supervisor"
                name="approval_code"
                type="text"
                value={pin}
                onChange={(e) => {
                  setPin(e.target.value);
                  if (errorMsg) setErrorMsg('');
                  if (contingencyMsg) setContingencyMsg('');
                }}
                autoFocus
                disabled={isSubmitting}
                autoComplete="off"
                spellCheck={false}
                autoCorrect="off"
                autoCapitalize="none"
                onKeyDownCapture={handleKeyDownCapture}
                onKeyUpCapture={handleKeyUpCapture}
                onKeyPressCapture={handleKeyPressCapture}
                inputProps={{
                  inputMode: 'text',
                  autoComplete: 'off',
                  'data-lpignore': 'true',
                  'data-form-type': 'other',
                  'aria-label': 'PIN de autorización',
                  style: {
                    WebkitTextSecurity: 'disc',
                    letterSpacing: '0.18em',
                    fontWeight: 700,
                  },
                }}
                helperText="Digite mínimo 4 caracteres alfanuméricos."
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 3,
                    backgroundColor: 'background.paper',
                  },
                }}
              />
            </Box>

            {contingencyMsg && (
              <Alert severity="warning" icon={<WifiOffRoundedIcon />} sx={{ borderRadius: 3 }}>
                {contingencyMsg}
              </Alert>
            )}

            {errorMsg && (
              <Alert severity="error" variant="filled" sx={{ borderRadius: 3, alignItems: 'center' }}>
                {errorMsg}
              </Alert>
            )}
          </Stack>
        </Box>
      </DialogContent>

      <Box
        sx={{
          px: 3,
          pb: 3,
          pt: 0,
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 1.25,
        }}
      >
        <Button
          onClick={onClose}
          disabled={isSubmitting}
          variant="outlined"
          sx={{ minWidth: 110, borderRadius: 999 }}
        >
          Cancelar
        </Button>

        <Button
          onClick={handleSubmit}
          disabled={isSubmitting || !pinSanitized}
          variant="contained"
          sx={{ minWidth: 170, borderRadius: 999, fontWeight: 700, boxShadow: 'none' }}
          startIcon={
            isSubmitting ? <CircularProgress size={18} color="inherit" /> : <SecurityRoundedIcon />
          }
        >
          {isSubmitting ? 'Verificando...' : 'Autorizar'}
        </Button>
      </Box>
    </Dialog>
  );
};

PinConfirmModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onVerified: PropTypes.func.isRequired,
  title: PropTypes.string,
  message: PropTypes.string,
};

export default PinConfirmModal;
